import * as Ably from 'ably';

export type RealtimeKind = 'wall' | 'dotvoting' | 'game';
export type RealtimeEvent = { topic: string; [key: string]: unknown };

export const realtimeChannelName = (kind: RealtimeKind, id: string) => `${kind}:${id}`;

type ChannelOptions<T extends RealtimeEvent> = {
  kind: RealtimeKind;
  id: string;
  getToken: () => Promise<string>;
  onMessage: (event: T) => void;
  onReady?: () => void;
  onContinuityLost?: () => void;
};

export function connectRealtimeChannel<T extends RealtimeEvent>(options: ChannelOptions<T>) {
  const client = new Ably.Realtime({
    echoMessages: false,
    authCallback: async (_params, callback) => {
      try {
        callback(null, await options.getToken());
      } catch (error) {
        callback(error instanceof Error ? error.message : 'Realtime authorization failed', null);
      }
    },
  });
  const channel = client.channels.get(realtimeChannelName(options.kind, options.id));
  let closed = false;
  let attachedBefore = false;

  const onChannelState = (change: Ably.ChannelStateChange) => {
    if (change.current === 'attached') {
      if (attachedBefore && !change.resumed) options.onContinuityLost?.();
      attachedBefore = true;
      options.onReady?.();
    }
  };
  channel.on(onChannelState);
  void channel
    .subscribe((message) => {
      const event = message.data as T;
      if (event && event.topic === options.id) options.onMessage(event);
    })
    .catch((error) => console.error('Realtime subscription failed', error));

  return {
    publish(event: T) {
      if (closed) return;
      void channel
        .publish('event', event)
        .catch((error) => console.error('Realtime publish failed', error));
    },
    close() {
      if (closed) return;
      closed = true;
      channel.off(onChannelState);
      client.close();
    },
  };
}
